import * as Dialog from '@radix-ui/react-dialog'
import { ChatLauncher } from '../ChatLauncher.jsx'
import { ChatPanel } from '../ChatPanel.jsx'
import { useChatLayout } from '../../../context/ChatLayoutContext.jsx'
import { cn } from '../../../lib/cn.js'
import {
  clampSlideOutPanelWidthPx,
  normalizeSlideOutPanelMaxHeightPx,
} from '../../../lib/slideOutPanelLayout.js'

/**
 * Side drawer panel. Slides in from the right edge on desktop and
 * occupies the full viewport on mobile. Uses Radix Dialog for the same
 * accessibility wins as `OverlayLayout`.
 */
export function SlideOutLayout({
  conf,
  messages,
  isTyping,
  onSend,
  ochatbot,
  container,
  onLeadFormSubmit,
  onOrderStatusResult,
  ...panelRest
}) {
  const { open, toggleChat, openChat, closeChat } = useChatLayout()
  const edge = String(conf?.slideOutPanelEdge || 'right').toLowerCase() === 'left' ? 'left' : 'right'
  const panelWidthPx = clampSlideOutPanelWidthPx(
    conf?.slideOutPanelWidth ?? conf?.ometricsAI?.slideOutPanelWidth,
  )
  const desktopMaxHeightPx = normalizeSlideOutPanelMaxHeightPx(
    conf?.slideOutPanelMaxHeight ?? conf?.ometricsAI?.slideOutPanelMaxHeight,
  )
  const smCappedHeight = desktopMaxHeightPx != null

  return (
    <Dialog.Root
      open={open}
      modal={false}
      onOpenChange={(next) => (next ? openChat() : closeChat())}
    >
      <ChatLauncher
        open={open}
        onToggle={toggleChat}
        label={conf?.widgetTitle || 'Chat with us'}
        conf={conf}
      />
      <Dialog.Portal container={container}>
        <Dialog.Overlay
          className={cn(
            'pointer-events-none fixed inset-0 z-[2147483645] bg-transparent',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in data-[state=closed]:fade-out',
          )}
        />
        <Dialog.Content
          aria-describedby={undefined}
          style={{
            ['--ometrics-slide-out-panel-width']: `${panelWidthPx}px`,
            ...(smCappedHeight
              ? {
                  ['--ometrics-slide-out-panel-max-height']: `${desktopMaxHeightPx}px`,
                }
              : {}),
          }}
          className={cn(
            'fixed z-[2147483647] flex min-h-0 w-full flex-col overflow-hidden border-[var(--color-border)] shadow-[var(--shadow-widget)]',
            'top-0 bottom-0',
            edge === 'right' && 'right-0 border-l',
            edge === 'left' && 'left-0 border-r',
            'sm:w-[min(100vw,var(--ometrics-slide-out-panel-width))] sm:max-w-[min(100vw,var(--ometrics-slide-out-panel-width))]',
            // Match overlay `rounded-2xl` on the inner edge only; viewport edge stays square.
            edge === 'right' && 'sm:rounded-l-2xl',
            edge === 'left' && 'sm:rounded-r-2xl',
            // Capped desktop height: anchor to the bottom (clear ~5rem for host page UI
            // such as a bottom close button). Do not use translate-y here — slide-in-from-*
            // owns `transform` on this node.
            smCappedHeight &&
              'sm:top-auto sm:bottom-20 sm:h-[min(var(--ometrics-slide-out-panel-max-height),calc(100dvh-5rem))] sm:max-h-[min(var(--ometrics-slide-out-panel-max-height),calc(100dvh-5rem))]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-300 data-[state=closed]:duration-300',
            edge === 'right' &&
              'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
            edge === 'left' &&
              'data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
            'ease-[cubic-bezier(0.4,0,0.2,1)]',
          )}
        >
          <Dialog.Title className="sr-only">
            {conf?.widgetTitle || 'Chat'}
          </Dialog.Title>
          <ChatPanel
            conf={conf}
            messages={messages}
            isTyping={isTyping}
            onSend={onSend}
            onClose={closeChat}
            ochatbot={ochatbot}
            className="min-h-0 flex-1 basis-0"
            onLeadFormSubmit={onLeadFormSubmit}
            onOrderStatusResult={onOrderStatusResult}
            {...panelRest}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
